import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import type { FormEvent, ReactNode } from 'react';
import { cx } from './cx';
import { Button } from './Button';
import { IconButton } from './IconButton';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Short line under the title. Consequences, not instructions. */
  description?: ReactNode;
  /**
   * Buttons. Cancel first, the confirming action last and primary, matching
   * the platform. On a phone Cancel is as wide as its word, the confirming
   * action takes the rest of the line (the two stack, full width, when its
   * words would not fit), and the footer stays on screen while the body
   * scrolls.
   */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Turn off when a click outside would lose typed work. */
  dismissOnBackdrop?: boolean;
  /**
   * Make the body and the footer one form. A `type="submit"` button in the
   * footer then submits it, so a form's buttons can live in the sticky
   * footer instead of at the end of the scrolling body.
   */
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  /** No body padding, for a sheet whose rows reach the edges. */
  flush?: boolean;
  children?: ReactNode;
}

const sizes = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl' } as const;

/*
 * The footer on a phone: Cancel and anything else beside it are as wide as
 * their words, the confirming action (the last child) takes the rest, and
 * when the words cannot all sit on one line the buttons stack, full width,
 * the confirming action last.
 *
 * It used to give every button an equal share (`flex: 1`, a basis of 0),
 * so on a 360px phone the confirming action had half of 320px and broke
 * its words over two lines: "Issue receipt no. 12", "Approve R 100 000,00",
 * "Log letter to the record", "Confirm and minute".
 *
 * Every child is now `flex: 1 1 auto`, so its starting width is its words
 * on one line, and a line breaks only when those widths do not fit side by
 * side. The last child grows a thousand times faster than the others, so
 * on a shared line it takes all but a fraction of a pixel of the space
 * left over, and Cancel stays the width of "Cancel". A child alone on its
 * line takes the whole line whatever its factor, which is what stacks
 * them full width once they wrap. A line that is not a button (a count
 * above the buttons) asks for its own line with `min-w-full`.
 */
const PHONE_FOOTER = cx(
  '[&>*]:flex-[1_1_auto] [&>:last-child]:flex-[1000_1_auto]',
  // Above 640px: right-aligned at their own widths, as always.
  'sm:[&>*]:flex-none sm:[&>:last-child]:flex-none',
);

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * A panel lifted off the page: surface, a hairline edge, a 14px radius and
 * the deepest shadow in the set. The backdrop is ink at 45%, nothing more:
 * the 2px backdrop blur it used to carry was the one GPU-heavy effect in
 * the app and cost frames during the rise on a R 1 500 Android. Traps
 * focus, closes on Escape, restores focus on close, locks background
 * scroll.
 *
 * Below 640px it is a bottom sheet rather than a box floating in the
 * middle of a phone: square bottom corners on the screen edge, a grab mark,
 * a height cap of 92% of the viewport, a body that scrolls on its own, and
 * a footer that stays put above the home indicator with the confirming
 * action a thumb's width (PHONE_FOOTER). Before this a long form scrolled
 * the whole overlay, so its buttons were below the fold and the bottom
 * corners were rounded against the edge of the glass.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  dismissOnBackdrop = true,
  onSubmit,
  flush,
  children,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);
  const keyboard = useKeyboardInset(open);
  // Per dialog, so a sheet opened over another dialog does not borrow its
  // title as its accessible name.
  const uid = useId();
  const titleId = `ct-dialog-title-${uid}`;
  const descId = `ct-dialog-desc-${uid}`;

  // Registered before paint, so a toast raised as a sheet closes never
  // shows for a frame in the place it takes while a sheet is open.
  const footerRef = useRef<HTMLElement>(null);
  const sheet = useRef<OpenSheet | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const entry = openSheets.add();
    sheet.current = entry;
    return () => {
      entry.close();
      sheet.current = null;
    };
  }, [open]);
  // Where this sheet's footer starts, counted up from the bottom of the
  // screen, so the toasts can sit just above it (see openSheets). On a
  // phone the sheet stands on the bottom edge, or on the keyboard.
  const hasFooter = Boolean(footer);
  const keyboardBottom = keyboard?.bottom ?? 0;
  useLayoutEffect(() => {
    const entry = sheet.current;
    const el = footerRef.current;
    if (!open || !entry) return;
    if (!el) {
      entry.place(null);
      return;
    }
    const report = () => entry.place(keyboardBottom + el.offsetHeight);
    report();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, hasFooter, keyboardBottom]);

  useEffect(() => {
    if (!open) return;
    returnFocusTo.current = document.activeElement as HTMLElement | null;

    // Move focus in, preferring the first control over the panel itself.
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    // The page behind must not scroll while a modal is up.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
      returnFocusTo.current?.focus?.();
    };
  }, [open]);

  // Escape and Tab are heard on the overlay, so they only reach the sheet
  // while the focus is inside it. A control that is disabled or removed
  // while it has the focus drops it on the page body: "Log letter to the
  // record" turns into a disabled "Logged to the record", a tap on the
  // sheet's padding blurs the field. The keyboard was then outside the
  // sheet, Escape did nothing and Tab walked the page behind the scrim.
  // With the focus on the body, the topmost sheet takes both back.
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    if (!open) return;
    const onLostKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'Tab') return;
      if (document.activeElement && document.activeElement !== document.body) return;
      const panel = panelRef.current;
      const modals = document.querySelectorAll('[aria-modal="true"]');
      if (!panel || modals[modals.length - 1] !== panel) return;
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      e.preventDefault();
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      (items[e.shiftKey ? items.length - 1 : 0] ?? panel).focus();
    };
    document.addEventListener('keydown', onLostKey);
    return () => document.removeEventListener('keydown', onLostKey);
  }, [open]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null,
      );
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!open) return null;

  const body = (
    <>
      {children ? (
        <div
          className={cx(
            'min-h-0 flex-1 overflow-y-auto overscroll-contain text-sm text-ink-2 sm:flex-none sm:overflow-visible',
            flush ? '' : 'px-5 py-4',
            // With no footer, the body is what sits over the home indicator.
            !footer && 'pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-4',
            !footer && flush && 'sm:pb-0',
          )}
        >
          {children}
        </div>
      ) : null}
      {footer ? (
        <footer
          ref={footerRef}
          className={cx(
            'flex shrink-0 flex-wrap justify-end gap-2 border-t border-line bg-paper-card px-5 pt-3',
            'pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-3',
            PHONE_FOOTER,
          )}
        >
          {footer}
        </footer>
      ) : null}
    </>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/45 sm:items-center sm:overflow-y-auto sm:p-4"
      style={keyboard ? { top: keyboard.top, bottom: keyboard.bottom } : undefined}
      onMouseDown={(e) => {
        if (dismissOnBackdrop && e.target === e.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cx(
          'relative flex w-full flex-col border border-line bg-paper-card shadow-dialog outline-none animate-rise',
          // Phone: a sheet on the bottom edge, capped, its own scroller.
          // 92% of what is visible: of the screen, or of what the
          // keyboard leaves of it when the overlay sits on the keyboard.
          'max-h-[min(92dvh,92%)] rounded-t-ct-lg rounded-b-none border-b-0',
          // Desk: the centred panel, as it always was.
          'sm:my-auto sm:max-h-none sm:rounded-ct-lg sm:border-b',
          sizes[size],
        )}
      >
        {/* The grab mark says "this slides up from the bottom" without a
            word. Decorative only: dismissal is the close button, Escape or
            the backdrop. */}
        <span aria-hidden="true" className="absolute left-1/2 top-2 h-1 w-9 -translate-x-1/2 rounded-full bg-line-strong sm:hidden" />
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-line px-5 pb-3 pt-5 sm:py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="font-display text-xl font-semibold leading-tight tracking-[-0.015em] text-ink [text-wrap:balance]">
              {title}
            </h2>
            {description ? (
              <p id={descId} className="mt-1 text-xs text-ink-3">
                {description}
              </p>
            ) : null}
          </div>
          <IconButton icon="close" label="Close" size="sm" onClick={onClose} className="-mr-2 -mt-1" />
        </header>
        {onSubmit ? (
          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col sm:flex-none">
            {body}
          </form>
        ) : (
          body
        )}
      </div>
    </div>,
    document.body,
  );
}

/** One open dialog, as the toasts see it. */
export interface OpenSheet {
  /** Where its footer starts, in px up from the bottom of the screen; null with no footer. */
  place(above: number | null): void;
  /** It closed. Safe to call twice. */
  close(): void;
}

/**
 * Where a phone's toasts go while a sheet is open: `null` with no sheet
 * open, a number of px up from the bottom of the screen to the top of the
 * topmost sheet's footer, or `'top'` when that sheet has no footer.
 */
export type ToastPlace = number | 'top' | null;

/*
 * The open dialogs, newest last, for the toasts.
 *
 * On a phone a toast sat just above the bottom bar, which is the top edge
 * of an open sheet's footer, so a "Failed" or a "Done" hid the top of the
 * buttons. The toasts then moved to the top of the screen while a sheet
 * was open, which on a sheet as tall as the screen allows (any long form
 * on a 360 by 640 Android) put the toast over the sheet's title and its
 * close button, with the toast's own dismiss cross where a thumb went for
 * the sheet's. They now sit just above the sheet's footer, the same rule
 * as above the bottom bar: over the end of the body, which scrolls, and
 * next to the button whose save the toast is about. A sheet with no
 * footer (a list of actions) keeps them at the top, clear of its rows.
 */
const sheetStack: { above: number | null }[] = [];
const sheetListeners = new Set<() => void>();
const notifySheets = () => {
  for (const listener of sheetListeners) listener();
};
export const openSheets = {
  add(): OpenSheet {
    const entry = { above: null as number | null };
    sheetStack.push(entry);
    notifySheets();
    return {
      place(above) {
        if (entry.above === above) return;
        entry.above = above;
        if (sheetStack.includes(entry)) notifySheets();
      },
      close() {
        const i = sheetStack.indexOf(entry);
        if (i === -1) return;
        sheetStack.splice(i, 1);
        notifySheets();
      },
    };
  },
  subscribe(listener: () => void): () => void {
    sheetListeners.add(listener);
    return () => {
      sheetListeners.delete(listener);
    };
  },
  toastPlace(): ToastPlace {
    const top = sheetStack[sheetStack.length - 1];
    if (!top) return null;
    return top.above ?? 'top';
  },
};

/** Where a phone's toasts go, given the dialogs open now (see openSheets). */
export function useToastPlace(): ToastPlace {
  return useSyncExternalStore(openSheets.subscribe, openSheets.toastPlace, () => null);
}

/**
 * Where the on-screen keyboard is, while a phone sheet is open.
 *
 * The sheet's footer is meant to stay on screen, and it did until a field
 * took the focus: Chrome on Android (since 108) and Safari on iOS do not
 * shrink the layout viewport for the keyboard, they lay it over the
 * bottom of the page, so a sheet anchored to the bottom edge kept its
 * Cancel and its confirming button under the keys. The visual viewport is
 * the part still showing; the overlay is pinned to it, so the sheet sits
 * on top of the keyboard. Null when there is nothing to move: no
 * keyboard, a desk width, a pinch zoom, or no visualViewport.
 */
function useKeyboardInset(open: boolean): { top: number; bottom: number } | null {
  const [inset, setInset] = useState<{ top: number; bottom: number } | null>(null);
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!open || !vv) return;
    const phone = window.matchMedia('(max-width: 639.98px)');
    const update = () => {
      const bottom = Math.round(window.innerHeight - vv.height - vv.offsetTop);
      const zoomed = Math.abs(vv.scale - 1) > 0.01;
      // Under 80px is browser chrome settling, not a keyboard.
      const next = phone.matches && !zoomed && bottom >= 80 ? { top: Math.round(vv.offsetTop), bottom } : null;
      setInset((prev) => (prev?.top === next?.top && prev?.bottom === next?.bottom ? prev : next));
      // The sheet got shorter, so keep the field being typed in on show.
      if (next) {
        const field = document.activeElement;
        if (field instanceof HTMLElement && field.closest('[role="dialog"]')) {
          requestAnimationFrame(() => field.scrollIntoView({ block: 'nearest' }));
        }
      }
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      setInset(null);
    };
  }, [open]);
  return inset;
}

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  /** Spell out what will happen. 'Are you sure?' tells nobody anything. */
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive turns the confirm button red and puts cancel first. */
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Replaces inline 'Yes / No' button pairs and window.confirm, which cannot be
 * styled or translated and reads as a browser warning rather than a decision
 * the app is asking for.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive,
  busy,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      dismissOnBackdrop={!busy}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body}
    </Dialog>
  );
}
